/**
 * SUR PRIMICIA CONTROL — Root PWA shell.
 * Bottom-nav mobile-first. Routes: /control/(home|articles|activity|alerts|more/*).
 * §16 — 5 secciones. Mobile-first + responsive.
 */
import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth, useOnlineStatus, controlApi } from "./lib/controlApi";
import { useBudgetAlertNotifier } from "./lib/budgetNotifier";
import {
  Home, Newspaper, Activity, Bell, MoreHorizontal, WifiOff, CheckCircle2,
  AlertTriangle,
} from "lucide-react";

import ControlLoginPage from "./pages/ControlLoginPage";
import ControlHomePage from "./pages/ControlHomePage";
import ControlArticlesPage from "./pages/ControlArticlesPage";
import ControlArticleDetailPage from "./pages/ControlArticleDetailPage";
import ControlActivityPage from "./pages/ControlActivityPage";
import ControlAlertsPage from "./pages/ControlAlertsPage";
import ControlMorePage from "./pages/ControlMorePage";
import ControlSystemPage from "./pages/ControlSystemPage";
import ControlSourcesPage from "./pages/ControlSourcesPage";
import ControlRunsPage from "./pages/ControlRunsPage";
import ControlSettingsPage from "./pages/ControlSettingsPage";
import ControlDevicesPage from "./pages/ControlDevicesPage";
import ControlLearningPage from "./pages/ControlLearningPage";
import ControlEditorPage from "./pages/ControlEditorPage";

/* Global error boundary: on ChunkLoadError, wipes caches + reloads once. */
class ControlErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidMount() {
    // Clear self-heal guard on a successful render so a later, unrelated
    // ChunkLoadError in the same tab session can still trigger the recovery.
    try { sessionStorage.removeItem("spc:reloaded_after_chunk_error"); } catch (_e) { /* noop */ }
  }
  componentDidCatch(err, info) {
    try {
      console.error("[Control ErrorBoundary]", err, info);
      // Chunk load errors after deploy → clear caches and reload once
      const msg = (err && (err.message || String(err))) || "";
      if (/ChunkLoadError|Loading chunk|Failed to fetch dynamically imported/.test(msg)) {
        const KEY = "spc:reloaded_after_chunk_error";
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, "1");
          if ("caches" in window) {
            caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
              .finally(() => window.location.reload());
          } else {
            window.location.reload();
          }
        }
      }
    } catch (_e) { /* noop */ }
  }
  handleReload = () => {
    try { sessionStorage.removeItem("spc:reloaded_after_chunk_error"); } catch (_e) { /* noop */ }
    if ("caches" in window) {
      caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .finally(() => window.location.reload());
    } else {
      window.location.reload();
    }
  };
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div data-testid="spc-error-boundary" className="min-h-screen bg-lightgray flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-softblue p-6 space-y-3 text-center">
          <div className="flex justify-center"><AlertTriangle className="w-8 h-8 text-amber-500" /></div>
          <div className="font-serif text-xl font-bold text-blueblack">No pudimos mostrar esta sección</div>
          <div className="text-xs text-textgray">
            Ocurrió un error al cargar el panel. Probá recargar; si persiste, cerrá sesión y volvé a entrar.
          </div>
          <button onClick={this.handleReload}
            data-testid="spc-error-reload"
            className="w-full h-10 bg-blueblack text-white text-xs font-bold uppercase tracking-widest">
            Recargar
          </button>
        </div>
      </div>
    );
  }
}

/* Register service worker + expose update prompt (§86-88). */
function useServiceWorker() {
  const [updateReady, setUpdateReady] = useState(null);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/control/sw.js", { scope: "/control/" }).then((reg) => {
      // Listen for waiting update
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            setUpdateReady(reg.waiting || nw);
          }
        });
      });
      // Handle in-app navigate messages from SW notificationclick
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "spc:navigate" && event.data.url) {
          window.location.href = event.data.url;
        }
      });
    }).catch(() => {});
  }, []);
  return updateReady;
}

function OfflineBanner() {
  const online = useOnlineStatus();
  if (online) return null;
  return (
    <div data-testid="spc-offline-banner" className="w-full bg-amber-500/90 text-black text-[12px] font-semibold px-3 py-1.5 flex items-center gap-2 justify-center">
      <WifiOff className="w-3.5 h-3.5" />
      <span>Sin conexión — mostrando últimos datos disponibles.</span>
    </div>
  );
}

function UpdateBanner({ reg }) {
  if (!reg) return null;
  const doUpdate = () => {
    reg.postMessage({ type: "spc:skip-waiting" });
    setTimeout(() => window.location.reload(), 400);
  };
  return (
    <div className="w-full bg-cyan-600 text-white text-[12px] font-semibold px-3 py-1.5 flex items-center gap-3 justify-center">
      <span>Nueva versión disponible.</span>
      <button onClick={doUpdate} className="underline">Actualizar</button>
    </div>
  );
}

function BottomNav({ unread }) {
  const linkCls = ({ isActive }) =>
    `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] uppercase tracking-widest font-semibold ${
      isActive ? "text-nightblue" : "text-textgray"
    }`;
  return (
    <nav data-testid="spc-bottom-nav" className="fixed bottom-0 inset-x-0 z-40 border-t border-softblue bg-white grid grid-cols-5 max-w-screen-md mx-auto">
      <NavLink end to="/control" className={linkCls} data-testid="nav-home">
        <Home className="w-5 h-5" /><span>Inicio</span>
      </NavLink>
      <NavLink to="/control/articles" className={linkCls} data-testid="nav-articles">
        <Newspaper className="w-5 h-5" /><span>Noticias</span>
      </NavLink>
      <NavLink to="/control/activity" className={linkCls} data-testid="nav-activity">
        <Activity className="w-5 h-5" /><span>Actividad</span>
      </NavLink>
      <NavLink to="/control/alerts" className={linkCls} data-testid="nav-alerts">
        <div className="relative">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="spc-alerts-badge" className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </div>
        <span>Alertas</span>
      </NavLink>
      <NavLink to="/control/more" className={linkCls} data-testid="nav-more">
        <MoreHorizontal className="w-5 h-5" /><span>Más</span>
      </NavLink>
    </nav>
  );
}

function Shell({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const reg = useServiceWorker();
  const location = useLocation();
  useBudgetAlertNotifier(user);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await controlApi.notificationCount();
        if (alive) setUnread(r.unread || 0);
      } catch { /* offline etc */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user, location.pathname]);

  return (
    <div className="min-h-screen bg-lightgray text-blueblack pb-20 max-w-screen-md mx-auto">
      <UpdateBanner reg={reg} />
      <OfflineBanner />
      {children}
      {user && <BottomNav unread={unread} />}
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/control/login" state={{ from: location }} replace />;
  return children;
}

function NoIndexTag() {
  useEffect(() => {
    const head = document.head;
    const ensure = (tag, sel, attrs) => {
      let el = head.querySelector(sel);
      if (!el) { el = document.createElement(tag); head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    // Robots
    ensure("meta", 'meta[name="robots"]', { name: "robots", content: "noindex, nofollow" });
    // Manifest → Control
    ensure("link", 'link[rel="manifest"]', { rel: "manifest", href: "/control/manifest.json" });
    // Apple touch icon (restaurado: ícono original de Sur Primicia)
    ensure("link", 'link[rel="apple-touch-icon"]', { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=2026020601" });
    // Meta específicas de la PWA
    ensure("meta", 'meta[name="apple-mobile-web-app-title"]', { name: "apple-mobile-web-app-title", content: "Sur Primicia Control" });
    ensure("meta", 'meta[name="application-name"]', { name: "application-name", content: "Sur Primicia Control" });
    ensure("meta", 'meta[name="apple-mobile-web-app-capable"]', { name: "apple-mobile-web-app-capable", content: "yes" });
    ensure("meta", 'meta[name="apple-mobile-web-app-status-bar-style"]', { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    ensure("meta", 'meta[name="mobile-web-app-capable"]', { name: "mobile-web-app-capable", content: "yes" });
    ensure("meta", 'meta[name="theme-color"]', { name: "theme-color", content: "#0b1b34" });
    // Canonical del portal debe removerse dentro de /control
    const canon = head.querySelector('link[rel="canonical"]');
    if (canon) canon.parentNode.removeChild(canon);
    // Título
    const prevTitle = document.title;
    document.title = "Sur Primicia Control";
    return () => { document.title = prevTitle; };
  }, []);
  return null;
}

export default function ControlApp() {
  return (
    <ControlErrorBoundary>
      <AuthProvider>
        <NoIndexTag />
        <Shell>
          <Routes>
            <Route path="/login" element={<ControlLoginPage />} />
            <Route path="/" element={<RequireAuth><ControlHomePage /></RequireAuth>} />
            <Route path="/articles" element={<RequireAuth><ControlErrorBoundary><ControlArticlesPage /></ControlErrorBoundary></RequireAuth>} />
            <Route path="/articles/:id" element={<RequireAuth><ControlArticleDetailPage /></RequireAuth>} />
            <Route path="/editor/new" element={<RequireAuth><ControlErrorBoundary><ControlEditorPage /></ControlErrorBoundary></RequireAuth>} />
            <Route path="/editor/article/:id" element={<RequireAuth><ControlErrorBoundary><ControlEditorPage /></ControlErrorBoundary></RequireAuth>} />
            <Route path="/editor/candidate/:id" element={<RequireAuth><ControlErrorBoundary><ControlEditorPage /></ControlErrorBoundary></RequireAuth>} />
            <Route path="/activity" element={<RequireAuth><ControlActivityPage /></RequireAuth>} />
            <Route path="/alerts" element={<RequireAuth><ControlAlertsPage /></RequireAuth>} />
            <Route path="/more" element={<RequireAuth><ControlMorePage /></RequireAuth>} />
            <Route path="/more/system" element={<RequireAuth><ControlSystemPage /></RequireAuth>} />
            <Route path="/more/sources" element={<RequireAuth><ControlSourcesPage /></RequireAuth>} />
            <Route path="/more/runs" element={<RequireAuth><ControlRunsPage /></RequireAuth>} />
            <Route path="/more/settings" element={<RequireAuth><ControlSettingsPage /></RequireAuth>} />
            <Route path="/more/devices" element={<RequireAuth><ControlDevicesPage /></RequireAuth>} />
            <Route path="/more/learning" element={<RequireAuth><ControlLearningPage /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/control" replace />} />
          </Routes>
        </Shell>
      </AuthProvider>
    </ControlErrorBoundary>
  );
}

/* Reusable design tokens for all pages */
export const STATUS_STYLE = {
  operational:  { label: "Operativo",     dot: "bg-emerald-500", text: "text-emerald-700", border: "border-emerald-300", bg: "bg-emerald-50" },
  degraded:     { label: "Degradado",     dot: "bg-amber-500",   text: "text-amber-700",   border: "border-amber-300",   bg: "bg-amber-50" },
  critical:     { label: "Crítico",       dot: "bg-red-500",     text: "text-red-700",     border: "border-red-300",     bg: "bg-red-50" },
  disconnected: { label: "Desconectado",  dot: "bg-red-500",     text: "text-red-700",     border: "border-red-300",     bg: "bg-red-50" },
  paused:       { label: "Pausado",       dot: "bg-slate-400",   text: "text-slate-600",   border: "border-slate-300",   bg: "bg-slate-100" },
  unknown:      { label: "Sin datos",     dot: "bg-slate-400",   text: "text-slate-600",   border: "border-slate-300",   bg: "bg-slate-100" },
};
export const OVERALL_MSG = {
  operational: { icon: CheckCircle2, title: "TODO FUNCIONA CORRECTAMENTE", sub: "Sistema automático operativo." },
  degraded:    { icon: null, title: "SISTEMA DEGRADADO", sub: "Uno o más componentes presentan problemas." },
  critical:    { icon: null, title: "REQUIERE ATENCIÓN", sub: "Se detectó un problema crítico." },
  paused:      { icon: null, title: "SISTEMA PAUSADO", sub: "El motor automático está pausado por el administrador." },
  unknown:     { icon: null, title: "SIN DATOS", sub: "No pudimos obtener el estado." },
};
